import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BuildingBanner from '../components/BuildingBanner';
import { Calendar, Plus, Edit, Trash2, MapPin, Clock, Search, Building2, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Evenements = () => {
  const { token, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [formData, setFormData] = useState({
    titre: '', description: '', date: '', heure: '', lieu: '', type: 'general', building_id: ''
  });

  useEffect(() => { 
    fetchEvents(); 
    fetchBuildings();
  }, [token]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`, { headers: { Authorization: `Bearer ${token}` } });
      setBuildings(response.data || []);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/evenements`, { headers: { Authorization: `Bearer ${token}` } });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/evenements/${editingEvent.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/evenements`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowModal(false);
      setEditingEvent(null);
      setFormData({ titre: '', description: '', date: '', heure: '', lieu: '', type: 'general', building_id: '' });
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      titre: event.titre || '', description: event.description || '',
      date: event.date?.split('T')[0] || '', heure: event.heure || '',
      lieu: event.lieu || '', type: event.type || 'general',
      building_id: event.building_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
      try {
        await axios.delete(`${API}/evenements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const getTypeColor = (type) => {
    const colors = { general: 'bg-blue-100 text-blue-700', reunion: 'bg-purple-100 text-purple-700', travaux: 'bg-orange-100 text-orange-700', fete: 'bg-green-100 text-green-700' };
    return colors[type] || colors.general;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getBuildingName = (buildingId) => {
    const building = buildings.find(b => b.id === buildingId);
    return building?.nom || '';
  };

  const filteredEvents = events.filter(e => {
    const matchesSearch = e.titre?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBuilding = !selectedBuilding || e.building_id === selectedBuilding;
    return matchesSearch && matchesBuilding;
  });

  const canManageEvents = ['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet'].includes(user?.role);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="evenements-page">
      {/* Building Banner for Copropriétaires */}
      {user?.role === 'coproprietaire' && (
        <BuildingBanner token={token} user={user} compact={true} />
      )}
      
      {/* Header - Style unifié */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <Calendar className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Événements</h1>
            <p className="text-gray-500">Planifiez et partagez les événements de vos copropriétés</p>
          </div>
        </div>
        {canManageEvents && (
          <button onClick={() => { setEditingEvent(null); setFormData({ titre: '', description: '', date: '', heure: '', lieu: '', type: 'general', building_id: '' }); setShowModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#00D3A8] text-white rounded-full font-medium shadow-md hover:bg-[#00B892] transition-all">
            <Plus size={18} /><span>Nouvel événement</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input type="text" placeholder="Rechercher un événement..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" />
        </div>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <select 
            value={selectedBuilding} 
            onChange={(e) => setSelectedBuilding(e.target.value)}
            className="pl-10 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm bg-white appearance-none min-w-[200px]"
            data-testid="building-filter"
          >
            <option value="">Tous les immeubles</option>
            {buildings.map(b => (
              <option key={b.id} value={b.id}>{b.nom}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3 md:space-y-4">
        {filteredEvents.map((event) => (
          <div key={event.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 hover:shadow-md transition-shadow">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex items-start space-x-3 md:space-x-4">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-[#00D3A8]/10 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                  <Calendar size={20} className="text-[#00D3A8]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-800 text-sm md:text-base">{event.titre}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${getTypeColor(event.type)}`}>{event.type}</span>
                  </div>
                  {event.building_id && getBuildingName(event.building_id) && (
                    <p className="text-xs text-[#00D3A8] font-medium mb-1 flex items-center gap-1">
                      <Building2 size={12} />
                      {getBuildingName(event.building_id)}
                    </p>
                  )}
                  <p className="text-xs md:text-sm text-gray-600 mb-2 line-clamp-2">{event.description}</p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs md:text-sm text-gray-500">
                    <span className="flex items-center"><Clock size={14} className="mr-1 flex-shrink-0" />{formatDate(event.date)} {event.heure && `à ${event.heure}`}</span>
                    {event.lieu && <span className="flex items-center"><MapPin size={14} className="mr-1 flex-shrink-0" /><span className="truncate">{event.lieu}</span></span>}
                  </div>
                </div>
              </div>
              {canManageEvents && (
                <div className="flex space-x-2 sm:flex-shrink-0">
                  <button onClick={() => handleEdit(event)} className="p-1.5 text-gray-400 hover:text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg transition-colors"><Edit size={18} /></button>
                  <button onClick={() => handleDelete(event.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={18} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredEvents.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm border border-gray-100">
          <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-sm">Aucun événement prévu</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-4 md:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg md:text-xl font-semibold mb-4">{editingEvent ? 'Modifier' : 'Nouvel'} événement</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Immeuble</label>
                <select value={formData.building_id} onChange={(e) => setFormData({...formData, building_id: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm"
                  data-testid="event-building-select">
                  <option value="">Tous les immeubles (général)</option>
                  {buildings.map(b => (
                    <option key={b.id} value={b.id}>{b.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input type="text" value={formData.titre} onChange={(e) => setFormData({...formData, titre: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure</label>
                  <input type="time" value={formData.heure} onChange={(e) => setFormData({...formData, heure: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
                <input type="text" value={formData.lieu} onChange={(e) => setFormData({...formData, lieu: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="general">Général</option>
                  <option value="reunion">Réunion</option>
                  <option value="travaux">Travaux</option>
                  <option value="fete">Fête</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1]">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Evenements;
